"use client";

import { useState } from "react";
import { CheckCircle, Circle, Clock, Loader2 } from "lucide-react";
import type { DealPayments, PaymentCheckboxKey } from "@/lib/deals/payment/types";
import {
  PAYMENT_CHECKBOX_LABELS,
  PAYMENT_CHECKBOX_KEYS,
  PAYMENT_CHECKBOX_DESCRIPTIONS,
  USER_PAYMENT_CHECKBOX_KEYS,
} from "@/lib/deals/payment/types";

interface PaymentChecklistProps {
  payment: DealPayments | null;
  dealId?: string;
  role?: "buyer" | "seller";
  onPaymentUpdated?: (payment: DealPayments) => void;
  className?: string;
}

/**
 * PaymentChecklist - Displays the 4-checkbox payment status for users
 * Checkboxes assigned to the user's role are interactive; others are read-only.
 */
export function PaymentChecklist({
  payment,
  dealId,
  role,
  onPaymentUpdated,
  className = "",
}: PaymentChecklistProps) {
  const [loadingKey, setLoadingKey] = useState<PaymentCheckboxKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  const completedCount = payment ? PAYMENT_CHECKBOX_KEYS.filter((key) => payment[key]).length : 0;

  const canToggle = (key: PaymentCheckboxKey): boolean => {
    if (!role || !dealId) return false;
    return (USER_PAYMENT_CHECKBOX_KEYS[role] as readonly PaymentCheckboxKey[]).includes(key);
  };

  const handleToggle = async (checkbox: PaymentCheckboxKey) => {
    if (!dealId || loadingKey) return;

    const currentValue = payment?.[checkbox] ?? false;
    setLoadingKey(checkbox);
    setError(null);

    try {
      const res = await fetch(`/api/deals/${dealId}/payment`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ checkbox, checked: !currentValue }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Fehler beim Aktualisieren");
      }

      const { data } = await res.json();
      onPaymentUpdated?.(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unbekannter Fehler");
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <div className={`rounded-lg border border-zinc-200 bg-white p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-zinc-900">Zahlungsstatus</h3>
        <span className="text-sm text-zinc-500">
          {completedCount} von {PAYMENT_CHECKBOX_KEYS.length} erledigt
        </span>
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {PAYMENT_CHECKBOX_KEYS.map((key) => {
          const isChecked = payment?.[key] ?? false;
          const checkedAt = payment?.[`${key}_at` as keyof DealPayments] as string | null;
          const isToggleable = canToggle(key);
          const isLoading = loadingKey === key;

          if (isToggleable) {
            return (
              <button
                key={key}
                onClick={() => handleToggle(key)}
                disabled={isLoading}
                className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${
                  isChecked ? "bg-emerald-50 hover:bg-emerald-100" : "bg-zinc-50 hover:bg-zinc-100"
                } disabled:opacity-50`}
              >
                <div
                  className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    isChecked ? "bg-emerald-500 text-white" : "bg-white border-2 border-zinc-300"
                  }`}
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isChecked ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : null}
                </div>

                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-medium ${
                      isChecked ? "text-emerald-700" : "text-zinc-700"
                    }`}
                  >
                    {PAYMENT_CHECKBOX_LABELS[key]}
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {PAYMENT_CHECKBOX_DESCRIPTIONS[key]}
                  </p>
                  {isChecked && checkedAt && (
                    <p className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(checkedAt).toLocaleDateString("de-DE", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  )}
                </div>
              </button>
            );
          }

          // Read-only checkbox
          return (
            <div
              key={key}
              className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                isChecked ? "bg-emerald-50" : "bg-zinc-50"
              }`}
            >
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isChecked ? "bg-emerald-500" : "bg-zinc-200"
                }`}
              >
                {isChecked ? (
                  <CheckCircle className="w-4 h-4 text-white" />
                ) : (
                  <Circle className="w-4 h-4 text-zinc-400" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-medium ${
                    isChecked ? "text-emerald-700" : "text-zinc-600"
                  }`}
                >
                  {PAYMENT_CHECKBOX_LABELS[key]}
                </p>
                {isChecked && checkedAt && (
                  <p className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(checkedAt).toLocaleDateString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {completedCount === PAYMENT_CHECKBOX_KEYS.length && (
        <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
          <p className="text-sm text-emerald-800 font-medium text-center">
            Alle Zahlungen abgeschlossen!
          </p>
        </div>
      )}
    </div>
  );
}
